// @ts-nocheck
/**
 * Segment evaluation utilities for matching leads against criteria
 */

/**
 * Evaluates a single criterion against a lead
 * @param {Object} lead - The lead object to evaluate
 * @param {Object} criteria - The criteria object with field, operator, and value
 * @returns {boolean} - Whether the lead matches the criteria
 */
export const evaluateCriteria = (lead, criteria) => {
  const { field, operator, value } = criteria;
  const leadValue = getLeadFieldValue(lead, field);

  switch (operator) {
    case 'equals':
      return leadValue === value;
    
    case 'contains':
      if (typeof leadValue === 'string' && typeof value === 'string') {
        return leadValue.toLowerCase().includes(value.toLowerCase());
      }
      if (Array.isArray(leadValue)) {
        return leadValue.some(item => 
          typeof item === 'string' && typeof value === 'string' 
            ? item.toLowerCase().includes(value.toLowerCase())
            : item === value
        );
      }
      return false;
    
    case 'greater_than':
      return Number(leadValue) > Number(value);
    
    case 'less_than':
      return Number(leadValue) < Number(value);
    
    case 'between':
      if (Array.isArray(value) && value.length === 2) {
        const numValue = Number(leadValue);
        return numValue >= Number(value[0]) && numValue <= Number(value[1]);
      }
      return false;
    
    case 'in':
      if (Array.isArray(value)) {
        if (Array.isArray(leadValue)) {
          return leadValue.some(item => value.includes(item));
        }
        return value.includes(leadValue);
      }
      return false;
    
    case 'not_in':
      if (Array.isArray(value)) {
        if (Array.isArray(leadValue)) {
          return !leadValue.some(item => value.includes(item));
        }
        return !value.includes(leadValue);
      }
      return true;
    
    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
};

/**
 * Gets the value of a field from a lead object
 * @param {Object} lead - The lead object
 * @param {string} field - The field name to retrieve
 * @returns {any} - The field value
 */
const getLeadFieldValue = (lead, field) => {
  // Handle nested field access if needed
  if (field.includes('.')) {
    return field.split('.').reduce((obj, key) => obj?.[key], lead);
  }
  return lead[field];
};

/**
 * Evaluates all criteria against a lead using logical operators
 * @param {Object} lead - The lead object to evaluate
 * @param {Array} criteriaList - Array of criteria objects
 * @returns {boolean} - Whether the lead matches all criteria
 */
export const evaluateSegmentCriteria = (lead, criteriaList) => {
  if (!criteriaList || criteriaList.length === 0) {
    return true; // No criteria means all leads match
  }

  // Group criteria by logical operator
  const groups = [];
  let currentGroup = [];
  
  criteriaList.forEach((criteria, index) => {
    currentGroup.push(criteria);
    
    // If this is the last criteria or the next one starts a new OR group
    if (index === criteriaList.length - 1 || 
        (index < criteriaList.length - 1 && criteriaList[index + 1].logicalOperator === 'OR')) {
      groups.push(currentGroup);
      currentGroup = [];
    }
  });

  // Evaluate each group (AND within groups, OR between groups)
  return groups.some(group => 
    group.every(criteria => evaluateCriteria(lead, criteria))
  );
};

/**
 * Filters leads based on segment criteria
 * @param {Array} leads - Array of lead objects
 * @param {Array} criteriaList - Array of criteria objects
 * @returns {Array} - Filtered array of leads that match the criteria
 */
export const filterLeadsByCriteria = (leads, criteriaList) => {
  if (!leads || !Array.isArray(leads)) {
    return [];
  }

  if (!criteriaList || criteriaList.length === 0) {
    return leads; // No criteria means all leads match
  }

  return leads.filter(lead => evaluateSegmentCriteria(lead, criteriaList));
};

/**
 * Calculates segment size and preview data
 * @param {Array} leads - Array of all leads
 * @param {Array} criteriaList - Array of criteria objects
 * @returns {Object} - Preview data with size, samples, and recommendations
 */
export const calculateSegmentPreview = (leads, criteriaList) => {
  const matchingLeads = filterLeadsByCriteria(leads, criteriaList);
  const size = matchingLeads.length;
  const totalLeads = leads.length;
  
  // Get sample profiles (up to 5)
  const sampleProfiles = matchingLeads.slice(0, 5);
  
  // Calculate quality score based on various factors
  const qualityScore = calculateQualityScore(size, totalLeads, criteriaList);
  
  // Generate recommendations
  const recommendations = generateRecommendations(size, totalLeads, criteriaList, matchingLeads);
  
  return {
    estimatedSize: size,
    sampleProfiles,
    qualityScore,
    recommendations,
    percentage: totalLeads > 0 ? Math.round((size / totalLeads) * 100) : 0
  };
};

/**
 * Calculates a quality score for the segment
 * @param {number} size - Number of matching leads
 * @param {number} totalLeads - Total number of leads
 * @param {Array} criteriaList - Array of criteria
 * @returns {number} - Quality score from 0-100
 */
const calculateQualityScore = (size, totalLeads, criteriaList) => {
  let score = 50; // Base score
  
  // Size factor (prefer segments that are not too small or too large)
  const percentage = totalLeads > 0 ? (size / totalLeads) * 100 : 0;
  if (percentage >= 5 && percentage <= 30) {
    score += 20; // Good size range
  } else if (percentage < 1) {
    score -= 30; // Too small
  } else if (percentage > 80) {
    score -= 20; // Too large, not targeted enough
  }
  
  // Criteria complexity (more specific criteria = higher quality)
  if (criteriaList.length >= 2) {
    score += 15;
  }
  if (criteriaList.length >= 4) {
    score += 10;
  }
  
  // Ensure score is within bounds
  return Math.max(0, Math.min(100, score));
};

/**
 * Generates recommendations for improving the segment
 * @param {number} size - Number of matching leads
 * @param {number} totalLeads - Total number of leads
 * @param {Array} criteriaList - Array of criteria
 * @param {Array} matchingLeads - Array of matching leads
 * @returns {Array} - Array of recommendation strings
 */
const generateRecommendations = (size, totalLeads, criteriaList, matchingLeads) => {
  const recommendations = [];
  const percentage = totalLeads > 0 ? (size / totalLeads) * 100 : 0;
  
  if (size === 0) {
    recommendations.push("No leads match your criteria. Try broadening your conditions.");
    recommendations.push("Consider using 'contains' instead of 'equals' for text fields.");
  } else if (percentage < 1) {
    recommendations.push("Your segment is very small. Consider relaxing some criteria.");
    recommendations.push("Try using 'OR' conditions to include more leads.");
  } else if (percentage > 80) {
    recommendations.push("Your segment includes most of your leads. Add more specific criteria for better targeting.");
    recommendations.push("Consider adding lead score or engagement criteria.");
  } else if (criteriaList.length === 1) {
    recommendations.push("Add more criteria to create a more targeted segment.");
    recommendations.push("Consider combining demographic and behavioral criteria.");
  }
  
  // Analyze lead distribution for specific recommendations
  if (matchingLeads.length > 0) {
    const statusDistribution = matchingLeads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {});
    
    const hotLeads = statusDistribution.hot || 0;
    if (hotLeads / matchingLeads.length > 0.7) {
      recommendations.push("Great! Your segment has a high concentration of hot leads.");
    }
  }
  
  return recommendations.slice(0, 3); // Limit to 3 recommendations
};

/**
 * Validates segment criteria with enhanced validation rules
 * @param {Array} criteriaList - Array of criteria objects
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateSegmentCriteria = (criteriaList) => {
  const errors = [];
  
  if (!criteriaList || criteriaList.length === 0) {
    errors.push("At least one criterion is required.");
    return { isValid: false, errors };
  }
  
  criteriaList.forEach((criteria, index) => {
    const criterionNumber = index + 1;
    
    // Field validation
    if (!criteria.field) {
      errors.push(`Criterion ${criterionNumber}: Field is required.`);
    }
    
    // Operator validation
    if (!criteria.operator) {
      errors.push(`Criterion ${criterionNumber}: Operator is required.`);
    }
    
    // Value validation based on operator type
    if (criteria.operator === 'between') {
      if (!Array.isArray(criteria.value) || criteria.value.length !== 2) {
        errors.push(`Criterion ${criterionNumber}: Between operator requires exactly 2 values.`);
      } else if (criteria.value[0] === '' || criteria.value[1] === '' || 
                 criteria.value[0] === null || criteria.value[1] === null) {
        errors.push(`Criterion ${criterionNumber}: Both values are required for between operator.`);
      } else {
        // Validate that first value is less than second for numeric/date fields
        const val1 = criteria.value[0];
        const val2 = criteria.value[1];
        if (!isNaN(val1) && !isNaN(val2) && Number(val1) >= Number(val2)) {
          errors.push(`Criterion ${criterionNumber}: First value must be less than second value for between operator.`);
        }
      }
    } else if (['in', 'not_in'].includes(criteria.operator)) {
      if (!Array.isArray(criteria.value) || criteria.value.length === 0) {
        const operatorLabel = criteria.operator === 'in' ? 'is one of' : 'is not one of';
        errors.push(`Criterion ${criterionNumber}: ${operatorLabel} operator requires at least one value.`);
      }
    } else {
      // Standard value validation for other operators
      if (criteria.value === undefined || criteria.value === null || criteria.value === '') {
        errors.push(`Criterion ${criterionNumber}: Value is required.`);
      }
    }
    
    // Logical operator validation (for criteria after the first one)
    if (index > 0 && !criteria.logicalOperator) {
      errors.push(`Criterion ${criterionNumber}: Logical operator is required for chained criteria.`);
    }
    
    // Field-specific validation
    if (criteria.field === 'leadScore') {
      const value = criteria.operator === 'between' ? criteria.value : [criteria.value];
      value.forEach(val => {
        if (val !== '' && val !== null && (isNaN(val) || Number(val) < 0 || Number(val) > 100)) {
          errors.push(`Criterion ${criterionNumber}: Lead score must be between 0 and 100.`);
        }
      });
    }
    
    if (criteria.field === 'lastEngaged' && criteria.value) {
      const dateValue = criteria.operator === 'between' ? criteria.value : [criteria.value];
      dateValue.forEach(val => {
        if (val && !isValidDate(val)) {
          errors.push(`Criterion ${criterionNumber}: Invalid date format for last engaged field.`);
        }
      });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Helper function to validate date strings
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - Whether the date is valid
 */
const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};